import React from 'react';
import { Link } from 'react-router-dom';
import { Logo } from '../components/Logo';

/**
 * The case for Heimdl, for the person who clicked "Learn more".
 *
 * Written for a school librarian who is almost certainly the only one — so it talks about her day,
 * not about features, and it never says "seamless", "powerful", "solution" or "revolutionise".
 * Petrol ground carries over from the front page so the two read as one place.
 */

const POINTS = [
  {
    title: 'Only the modules you need',
    body: `Cataloguing, circulation, overdues, billing, labels, MARC export, bookings, special
      collections. Turn on the ones your library actually runs and leave the rest off — an empty
      menu item is a daily reminder of work you are not doing.`,
  },
  {
    title: 'Built by a librarian, for librarians',
    body: `Not adapted from a public-library system and not designed by people who have never
      processed a returns trolley. Every screen exists because the work exists, and the vocabulary
      on it is the vocabulary you already use.`,
  },
  {
    title: 'Fast enough for one person',
    body: `Most school libraries are run by one person, often part-time, usually with a class
      waiting. Scan a barcode and the record, cover, reading level and call number arrive filled
      in — you check them and move on. Hundreds of books in an afternoon, not a term.`,
  },
  {
    title: 'Yours to configure',
    body: `Shelving sections, call-number rules, loan lengths per grade, barcode ranges, patron
      types, the cataloguing form itself. None of it is hard-coded to one school, because your
      library is not a copy of anyone else's.`,
  },
  {
    title: 'Automated where it helps, quiet where it does not',
    body: `Overdues escalate on their own, due dates roll off days the library is shut, teachers
      book the library from a link. Nothing emails a family without you, and nothing decides a
      child owes money on its own.`,
  },
  {
    title: 'Made for how schools are actually organised',
    body: `Grades, homerooms and teachers rather than membership numbers. Year rollover moves every
      child up, graduates the top year and hands finance one spreadsheet — the week in August that
      usually eats itself.`,
  },
];

function LearnMore() {
  return (
    <div className="min-h-screen bg-petrol text-white">
      <header className="px-6 sm:px-10 py-6 flex items-center justify-between border-b border-white/15">
        <Link to="/" className="hover:opacity-70 transition-opacity">
          <Logo size={28} showText />
        </Link>
        <Link
          to="/login"
          className="text-body tracking-[0.08em] uppercase hover:opacity-70 transition-opacity"
        >
          Log in
        </Link>
      </header>

      <main className="max-w-4xl mx-auto px-6 sm:px-10 py-16 sm:py-24">
        <h1 className="font-display text-[30px] sm:text-[44px] leading-[1.15] max-w-2xl">
          A library system that assumes there is one of you.
        </h1>
        <p className="text-input mt-6 max-w-2xl opacity-80">
          Heimdl is modular: you run the parts of a library system your school actually has, and the
          rest stays out of your way until you want it.
        </p>

        <div className="grid sm:grid-cols-2 gap-x-12 gap-y-10 mt-16">
          {POINTS.map((p) => (
            <section key={p.title}>
              <h2 className="font-display text-section">{p.title}</h2>
              <p className="text-body mt-3 opacity-80 leading-relaxed">{p.body}</p>
            </section>
          ))}
        </div>

        <section className="mt-20 pt-10 border-t border-white/15">
          <h2 className="font-display text-section">Moving from another system?</h2>
          <p className="text-body mt-3 max-w-2xl opacity-80 leading-relaxed">
            Your catalogue comes across through the front door. Export your MARC records and every
            title, copy, barcode and shelf arrives intact, accents and all. Nothing is retyped, and
            the barcodes already on your books stay exactly as they are.
          </p>
          <div className="flex flex-wrap items-center gap-4 mt-8">
            <Link
              to="/login"
              className="bg-white text-petrol text-input px-8 py-3 rounded-[2px] hover:opacity-90 transition-opacity"
            >
              Log in
            </Link>
            <Link
              to="/"
              className="border border-white/60 text-white text-input px-8 py-3 rounded-[2px] hover:bg-white/10 transition-colors"
            >
              Back
            </Link>
          </div>
        </section>
      </main>
    </div>
  );
}

export default LearnMore;
